import {
  COMMENTS_CATEGORY,
  CREATOR_CATEGORY,
  SIGNOFFS_ID,
  STAGE_CATEGORY,
  VERSION_CATEGORY,
} from 'features/libra/dashboard/ticketDashboard/libra/pills/categoryIds'
import {
  type SignoffPills,
  SIGNOFFS_GROUP,
  type SlotState,
} from 'features/libra/dashboard/ticketDashboard/libra/pills/signoffPills'
import type { StagePills } from 'features/libra/dashboard/ticketDashboard/libra/pills/stagePills'
import type {
  BoardSettings,
  BuiltInPillSettings,
  FieldPillSettings,
  TicketDashboardSettings,
} from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import type {
  BoardConfig,
  PillCategories,
  PillCategory,
  PillOption,
  TicketDashboardConfig,
} from 'features/libra/dashboard/ticketDashboard/types'

const DEFAULT_BOARDS: ReadonlyArray<BoardSettings> = [
  { id: STAGE_CATEGORY, otherColumn: undefined },
]
const COMMENT_OPTIONS: ReadonlyArray<PillOption> = [
  { value: 'unresolved', label: 'Has unresolved comments', tone: 'warning' },
]

function builtInCategory(
  defaults: PillCategory,
  settings: BuiltInPillSettings
): PillCategory {
  return {
    ...defaults,
    filter: settings.filter ?? defaults.filter,
  }
}

function fieldPillOptions(field: FieldPillSettings): PillOption[] {
  return field.values.map(({ value, label, tone }) => ({
    value,
    label: label ?? value,
    tone,
  }))
}

interface WorkflowPills {
  stagePills: StagePills
  signoffPills: SignoffPills
}

/**
 * Built-in defaults: only Stage and Created by filter out of the box; Sign-offs
 * and Comments filter when `builtIns.<pill>.filter` is set.
 */
function buildCategories(
  settings: TicketDashboardSettings,
  { stagePills, signoffPills }: WorkflowPills
): PillCategories {
  const { stage, version, creator, signoffs, comments } = settings.builtIns
  const categories: PillCategories = {}
  if (stage.enabled) {
    categories[STAGE_CATEGORY] = builtInCategory(
      { label: 'Stage', filter: true, options: stagePills.options },
      stage
    )
  }
  if (version.enabled) {
    categories[VERSION_CATEGORY] = builtInCategory(
      { label: 'Version' },
      version
    )
  }
  if (creator.enabled) {
    categories[CREATOR_CATEGORY] = builtInCategory(
      { label: 'Created by', filter: true },
      creator
    )
  }
  if (signoffs.enabled) {
    for (const slot of signoffPills.slots) {
      categories[slot.categoryId] = builtInCategory(
        {
          label: `${slot.label} sign-off`,
          group: SIGNOFFS_GROUP,
          options: signoffPills.options,
        },
        signoffs
      )
    }
  }
  if (comments.enabled) {
    categories[COMMENTS_CATEGORY] = builtInCategory(
      { label: 'Comments', options: COMMENT_OPTIONS },
      comments
    )
  }
  for (const field of settings.fields) {
    categories[field.id] = {
      label: field.label,
      icon: field.icon,
      filter: field.filter,
      showOnlyOnModal: field.showOnlyOnModal,
      options: fieldPillOptions(field),
    }
  }
  return categories
}

const AWAITING: SlotState = 'awaiting'

/** One column per enabled sign-off slot, holding cards whose slot is awaiting a signature. */
function missingSignoffColumns(
  signoffPills: SignoffPills,
  categories: PillCategories
) {
  return signoffPills.slots
    .filter((slot) => categories[slot.categoryId])
    .map((slot) => ({
      id: slot.id,
      label: `${slot.label} missing`,
      tone: 'warning' as const,
      match: { [slot.categoryId]: [AWAITING] },
    }))
}

function columnsFor(category: string, options: ReadonlyArray<PillOption>) {
  return options.map((option) => ({
    id: option.value,
    label: option.label,
    tone: option.tone,
    match: { [category]: [option.value] },
  }))
}

/**
 * A board for any category with known options, or `signoffs` for one column
 * per missing slot. Unknown ids and field pills without known values are
 * skipped.
 */
function buildBoard(
  board: BoardSettings,
  categories: PillCategories,
  { signoffPills }: WorkflowPills
): BoardConfig | undefined {
  const otherColumn = board.otherColumn
    ? { label: board.otherColumn }
    : undefined
  if (board.id === SIGNOFFS_ID) {
    const columns = missingSignoffColumns(signoffPills, categories)
    if (columns.length === 0) return undefined
    return { id: board.id, label: 'Missing sign-offs', columns, otherColumn }
  }
  const category = categories[board.id]
  if (!category?.options?.length) return undefined
  return {
    id: board.id,
    label: category.label,
    columns: columnsFor(board.id, category.options),
    otherColumn,
  }
}

/** Depends only on settings and workflows, so it stays stable while tickets refresh. */
export function buildDashboardConfig(
  dashboardId: string,
  settings: TicketDashboardSettings,
  workflowPills: WorkflowPills
): TicketDashboardConfig {
  const categories = buildCategories(settings, workflowPills)
  const boards = (settings.boards ?? DEFAULT_BOARDS).flatMap(
    (board) => buildBoard(board, categories, workflowPills) ?? []
  )
  return { id: dashboardId, categories, boards }
}
